import { ChangeDetectionStrategy, Component, computed, DestroyRef, OnInit, signal } from '@angular/core';
import * as MenuActions from '@states/menu/menu.actions';
import { MenuKey } from '@enums/menu.enum';
import { Store } from '@ngrx/store';
import { LocationEditSelectors } from '@states/location-edit/location-edit.selector-types';
import { DeviceModel } from '@models/device.model';
import { LocationEditActions } from '@states/location-edit/location-edit.action-types';
import { ActivatedRoute, Router } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { Dictionary } from '@ngrx/entity/src/models';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { CameraPickerModels } from '../../../modals/camera-picker-inline/models/models';
import { CameraPickerModalComponent } from '../../../modals/camera-picker-modal/camera-picker-modal.component';
import { UiKitModule } from '../../../shared/ui-kit/ui-kit.module';
import { TranslatePipe } from '@ngx-translate/core';
import { syncQueryParam } from '../../../helpers/sync-query-param.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import DeviceType = DeviceModel.DeviceType;
import { UiSearchComponent } from '../../../shared/ui-kit/ui-search/ui-search.component';
import { features } from '@consts/text.const';
import { UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from '../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createLocationSpeakersTableConfig } from './location-speakers-table.util';

type ViewState = 'LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';

@Component({
  selector: 'app-location-speakers',
  templateUrl: './location-speakers.component.html',
  styleUrls: ['./location-speakers.component.scss'],
  imports: [
    UiKitModule,
    TranslatePipe,
    UiSearchComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiDatasetTableComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationSpeakersComponent implements OnInit {
  protected readonly noResults = features.locationSpeakers.noResults;
  protected readonly noData = features.locationSpeakers.noData;

  public readonly vm$ = computed(() => {
    const isLoading = this.store$.selectSignal(LocationEditSelectors.selectedIsLoading)();
    const allSpeakers = this.speakers();
    const filteredSpeakers = this.filteredSpeakers();
    const query = this.query();

    let viewState: ViewState;
    if (isLoading && !allSpeakers.length) {
      viewState = 'LOADING';
    } else if (allSpeakers.length > 0 && query && !filteredSpeakers.length) {
      viewState = 'NO_RESULTS';
    } else if (allSpeakers.length > 0) {
      viewState = 'HAS_DATA';
    } else {
      viewState = 'INITIAL_EMPTY';
    }

    return {
      viewState,
      filteredSpeakers,
      query,
    };
  });

  public readonly tableConfig = computed(() => createLocationSpeakersTableConfig());

  protected query = syncQueryParam<string>('search');

  private filteredSpeakers = computed(() => {
    const query = this.query();
    const speakers = this.speakers();
    if (!query) {
      return speakers;
    }
    return speakers.filter((speaker) => speaker.name.toLowerCase().includes(query.toLowerCase()));
  });

  private speakers = signal<DeviceModel.LocationDeviceDocument[]>([]);

  constructor(
    private store$: Store,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.speakers,
      }),
    );

    this.store$
      .select(LocationEditSelectors.selectedLocation)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((location) => {
        if (location?.devices) {
          this.speakers.set(
            Object.values(location?.devices).filter((device) => device.type === DeviceType.Speaker),
          );
        } else {
          this.speakers.set([]);
        }
      });
  }

  public edit(speaker: DeviceModel.LocationDeviceDocument) {
    this.router.navigate(['../', routerSegments.editSpeaker, speaker._id], { relativeTo: this.route });
  }

  public delete(speaker: DeviceModel.LocationDeviceDocument) {
    this.store$.dispatch(LocationEditActions.deleteSpeaker({ deviceId: speaker._id }));
  }

  public selectCamera(orig: DeviceModel.LocationDeviceDocument): void {
    const row = _.cloneDeep(orig);
    if (!row?.cameras) {
      row.cameras = {};
    }
    const cams = _.cloneDeep(row.cameras);
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: {
        multi: true,
      },
      selectedValue: cams,
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '70vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .subscribe((selectedCameras: Dictionary<CameraPickerModels.SelectedCamera>) => {
        if (selectedCameras) {
          row.cameras = selectedCameras;
          this.store$.dispatch(LocationEditActions.setSelectedSpeaker({ selectedSpeaker: row }));
          this.store$.dispatch(LocationEditActions.addSpeaker());
        }
      });
  }

  public navigateToCreate(): void {
    this.router.navigate(['../', routerSegments.addSpeaker], { relativeTo: this.route });
  }

  public onRowAction(event: { actionId: string; row: DeviceModel.LocationDeviceDocument }): void {
    switch (event.actionId) {
      case 'manage-cameras':
        this.selectCamera(event.row);
        break;
      case 'edit':
        this.edit(event.row);
        break;
      case 'delete':
        this.delete(event.row);
        break;
    }
  }
}
